import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AddFilled, DeleteRegular, EditRegular } from '@fluentui/react-icons';
import { Box, Button, ButtonBase, Card, IconButton, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddVehicleDialog, ConfirmDialog } from '@ui/components/Dialogs';
import { clearFilters, clearVehicles, fetchVehicles, setAddFilter, setIsBlackListed, setPageNumber, setPageSize, setSearch } from '@application/redux/whitelistBlacklistVehicle/vehicles-slice';
import { FiltersDiscardButton } from '@ui/components/common/buttons';
import { SDataGrid } from '@ui/components/common/grid/SDataGrid';
import { useDispatch, useSelector } from 'react-redux';
import useBlacklistWhitelistStyles from './useBlacklistWhitelistStyles';
import { deleteVehicleDetailAction } from '@application/redux/actions/vehicle-detail-actions';
import { toast } from 'react-toastify';
import SSearchInput from '@ui/components/common/SSearchInput';
var ListType;
(function (ListType) {
    ListType["Blacklist"] = "Blacklist";
    ListType["Whitelist"] = "Whitelist";
})(ListType || (ListType = {}));
const BlacklistWhitelist = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { mainContainerStyles, tabsSwitchContainerStyles, buttonStyles, toolbarStyles, filterSectionStyles, contentContainerStyles, addVehicleButtonStyles, imageStyles } = useBlacklistWhitelistStyles();
    const dispatch = useDispatch();
    const [selectedTab, setSelectedTab] = useState(ListType.Blacklist);
    const [showAddVehicleDialog, setShowAddVehicleDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedListedItemId, setSelectedListedItemId] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFilterEnabled, setIsFilterEnabled] = useState(false);
    const { vehicles, loading, search, pageNumber, pageSize, isBlackListed, filteredColumns } = useSelector((state) => state.blacklistWhitelistVehicle);
    useEffect(() => {
        dispatch(fetchVehicles());
        return () => {
            dispatch(clearVehicles());
        };
    }, [dispatch, search, pageNumber, pageSize, isBlackListed, filteredColumns]);
    const handleTabSwitch = (listingType) => {
        setSelectedTab(listingType);
    };
    const isSelected = useCallback((listingType) => selectedTab === listingType, [selectedTab]);
    const handleOpenAddVehicleDialog = () => {
        setShowAddVehicleDialog(true);
    };
    const handleCloseAddVehicleDialog = () => {
        setShowAddVehicleDialog(false);
        dispatch(fetchVehicles());
    };
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            if (itemToDelete) {
                var response = await dispatch(deleteVehicleDetailAction(itemToDelete.id));
                if (response?.isSuccess) {
                    toast.success(response?.message);
                    dispatch(fetchVehicles());
                }
                else {
                    toast.error(response?.message);
                }
            }
        }
        catch (error) {
            toast.error(t('FailedNotesError'));
        }
        finally {
            setIsConfirmDialogOpen(false);
            setIsDeleting(false);
        }
    };
    const handleEnableFilters = () => {
        setIsFilterEnabled(true);
    };
    const DiscardAndCloseFilters = () => {
        setIsFilterEnabled(false);
        dispatch(clearFilters());
    };
    const handleFilters = (filters) => {
        const filtersArray = Object.entries(filters)
            .filter(([_, value]) => (value ?? '').trim() !== '')
            .map(([key, value]) => ({ key, value }));
        if (filtersArray.length > 0) {
            dispatch(setAddFilter(filtersArray));
        }
        else {
            dispatch(setAddFilter([]));
        }
    };
    const rows = vehicles?.data.data.map((vehicle) => ({
        id: vehicle.id || '',
        image: vehicle.imageName ? `${vehicle.imageUrl}` : '',
        licensePlate: vehicle.licensePlate,
        vehicleType: vehicle.vehicleType || '',
        make: vehicle.make,
        variant: vehicle.variant || '',
        model: vehicle.model ? String(vehicle.model) : '0',
        color: vehicle.color || ''
    })) || [];
    useEffect(() => {
        return () => {
            dispatch(clearFilters());
        };
    }, [dispatch]);
    const columns = [
        {
            field: 'image',
            headerName: '',
            minWidth: 52,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return params.value ? (_jsx("img", { src: params.value, alt: 'incident-preview', style: imageStyles })) : null;
            }
        },
        {
            field: 'licensePlate',
            headerName: 'License #',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'vehicleType',
            headerName: 'Vehicle Type',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'make',
            headerName: 'Make',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'variant',
            headerName: 'Brand',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'model',
            headerName: 'Model',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'color',
            headerName: 'Color',
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: '',
            headerName: '',
            minWidth: 110,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, pr: '6px' }, children: [_jsx(IconButton, { onClick: () => {
                                setSelectedListedItemId(params.row.id);
                                setIsEdit(true);
                            }, children: _jsx(EditRegular, { fontSize: 20, color: theme.palette.info.main }) }), _jsx(IconButton, { onClick: () => {
                                setItemToDelete(params.row);
                                setIsConfirmDialogOpen(true);
                            }, children: _jsx(DeleteRegular, { fontSize: 20, color: theme.palette.error.main }) })] }));
            }
        }
    ];
    const hasChanges = true;
    const hasNewChanges = isFilterEnabled && hasChanges;
    return (_jsxs(Card, { className: 'section section__blacklist-whitelist', sx: mainContainerStyles, children: [_jsxs(Box, { className: 'switch-tabs-container', sx: tabsSwitchContainerStyles, children: [_jsx(ButtonBase, { className: `blacklist-btn ${isSelected(ListType.Blacklist) ? 'active' : ''}`, sx: buttonStyles, onClick: () => {
                            handleTabSwitch(ListType.Blacklist);
                            dispatch(setIsBlackListed(true));
                        }, children: _jsx(Typography, { variant: 'subtitle1', children: ListType.Blacklist }) }), _jsx(ButtonBase, { className: `whitelist-btn ${isSelected(ListType.Whitelist) ? 'active' : ''}`, sx: { ...buttonStyles, left: -30 }, onClick: () => {
                            handleTabSwitch(ListType.Whitelist);
                            dispatch(setIsBlackListed(false));
                        }, children: _jsx(Typography, { variant: 'subtitle1', children: ListType.Whitelist }) })] }), _jsxs(Box, { className: 'content-container', sx: contentContainerStyles, children: [_jsxs(Box, { className: 'toolbar-container', sx: toolbarStyles, children: [_jsxs(Box, { className: 'filter-section', sx: filterSectionStyles, children: [_jsx(SSearchInput, { onChange: (e) => {
                                            dispatch(setSearch(e.target.value));
                                        } }), _jsx(FiltersDiscardButton, { hasNewChanges: hasNewChanges, handleDiscardFilters: DiscardAndCloseFilters, handleShowFilters: handleEnableFilters })] }), _jsx(Button, { className: 'btn__add-vehicle', sx: addVehicleButtonStyles, variant: 'contained', color: 'inherit', size: 'medium', onClick: handleOpenAddVehicleDialog, startIcon: _jsx(AddFilled, { fontSize: 20 }), children: t('AddVehicle') })] }), _jsx(Box, { className: 'table-container', sx: { height: { md: 'calc(100dvh - 450px)', lg: 'calc(100dvh - 350px)' } }, children: _jsx(SDataGrid, { rows: rows, columns: columns, serverSidePagination: true, totalRows: vehicles?.data.totalRecords, showFilters: isFilterEnabled, handleEnableFilters: handleEnableFilters, onFilterChange: handleFilters, pageSizeOptions: [10, 20, 50, 100], initialState: {
                                pagination: {
                                    paginationModel: {
                                        pageSize: vehicles?.data.pageSize || 10,
                                        page: vehicles?.data.pageNumber || 0
                                    }
                                }
                            }, onPaginationModelChange: (model) => {
                                dispatch(setPageNumber(model.page + 1));
                                dispatch(setPageSize(model.pageSize));
                            }, loading: loading, filters: {}, setFilters: () => { } }) })] }), selectedListedItemId && (_jsx(AddVehicleDialog, { onClose: () => {
                    setIsEdit(false);
                    dispatch(fetchVehicles());
                }, isOpen: isEdit, variant: 'edit', listedCarId: selectedListedItemId })), showAddVehicleDialog && (_jsx(AddVehicleDialog, { onClose: () => {
                    handleCloseAddVehicleDialog();
                }, isOpen: showAddVehicleDialog, variant: 'add' })), _jsx(ConfirmDialog, { isOpen: isConfirmDialogOpen, onClose: () => setIsConfirmDialogOpen(false), onConfirm: handleDelete, loading: isDeleting, actionText: t('DeleteCarText', { action: { selectedTab } }), screenName: itemToDelete ? itemToDelete.licensePlate : '' })] }));
};
export default BlacklistWhitelist;
